/* The Hustlers Deck — scroll-driven poster line-up.
   Requires gsap.min.js + gsap-scrolltrigger.min.js (vendored in assets/).
   Deliberately simple: no 3D, no flips. Posters slide straight up from
   below the stage into a grid, one after another (each starts when the
   previous is halfway up). Desktop = 3 columns × 2 rows, mobile = 2 columns
   × 3 rows. Resting cards keep at most ±1° tilt. The first poster starts at
   scroll progress 0, so the reveal begins as soon as the section pins.
   Scrolling back slides them out again. Desktop adds a registration-mark
   cursor ring and a hover tilt. Static grid remains for reduced motion / no
   JS; breakpoints are handled by gsap.matchMedia. */
(function () {
  'use strict';

  var CART_ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* resting tilt, max ±1° — just enough to feel hand-placed */
  var REST_ROT = [-0.9, 0.7, -0.5, 0.9, -0.7, 0.5];

  var RISE = 1;            /* per-card rise duration (timeline units) */
  var OVERLAP = 0.5;       /* next card starts at 50% of the previous rise */
  var UNIT_VH = 42;        /* scroll distance (vh) per timeline unit */

  function setupQuickAdd(root) {
    var labels = {
      quickadd: root.dataset.labelQuickadd || '[ IN DEN WARENKORB ]',
      added: root.dataset.labelAdded || '[ DRIN. ]',
      soldout: root.dataset.labelSoldout || '[ AUSVERKAUFT ]',
      error: root.dataset.labelError || '[ NOCHMAL VERSUCHEN ]'
    };

    root.querySelectorAll('[data-deck-quickadd]').forEach(function (btn) {
      var span = btn.querySelector('span');

      if (btn.dataset.available !== 'true') {
        span.textContent = labels.soldout;
        btn.disabled = true;
        return;
      }

      btn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (btn.classList.contains('is-busy')) return;
        btn.classList.add('is-busy');

        fetch(CART_ROOT + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: Number(btn.dataset.variantId),
            quantity: 1
          })
        })
          .then(function (res) {
            if (!res.ok) throw new Error('cart add failed: ' + res.status);
            return res.json();
          })
          .then(function (item) {
            span.textContent = labels.added;
            btn.classList.add('is-added');
            document.dispatchEvent(
              new CustomEvent('valismo:cart:added', { detail: item })
            );
          })
          .catch(function () {
            span.textContent = labels.error;
            btn.classList.add('is-error');
          })
          .then(function () {
            setTimeout(function () {
              span.textContent = labels.quickadd;
              btn.classList.remove('is-added', 'is-error', 'is-busy');
            }, 1600);
          });
      });
    });
  }

  /* card-translate Y that places a card at viewport Y `ty`, whatever the
     stage's own position (its center can sit below the viewport center once
     the header takes the top of the pinned view) */
  function viewportY(root, stage, ty) {
    var stageCenterInRoot =
      stage.getBoundingClientRect().top -
      root.getBoundingClientRect().top +
      stage.offsetHeight / 2;
    return ty - stageCenterInRoot;
  }

  /* header bottom as a viewport Y for the pinned section (top:0). Computed
     as a rect difference against the root so it is scroll/pin-independent,
     the same trick viewportY uses. */
  function headerBottomVp(root) {
    var header = root.querySelector('.hustlers-deck__header');
    if (!header) return window.innerHeight * 0.2;
    return (
      header.getBoundingClientRect().bottom - root.getBoundingClientRect().top
    );
  }

  /* a grid slot factory. layout(i, n) -> {x, y, scale} from the stage center.
     The grid auto-fits into the zone BELOW the header and centers there, so
     the top row always clears the heading whatever its height. Cards fill
     row by row, left to right (reading order). */
  function makeGrid(root, stage, cards, cols, opts) {
    return function (i, n) {
      var cardW = cards[0].offsetWidth;
      var cardH = cards[0].offsetHeight || cardW * 1.55;
      var rows = Math.ceil(n / cols);

      var zoneTop = headerBottomVp(root) + opts.zonePad;
      var zoneBottom = window.innerHeight - opts.zonePad;
      var zoneCenter = (zoneTop + zoneBottom) / 2;

      var availW = stage.offsetWidth - opts.padX;
      var availH = zoneBottom - zoneTop;
      var wScale = (availW - (cols - 1) * opts.gap) / cols / cardW;
      var hScale = (availH - (rows - 1) * opts.gap) / rows / cardH;
      var scale = Math.min(opts.maxScale, wScale, hScale);
      var slotW = cardW * scale + opts.gap;
      var slotH = cardH * scale + opts.gap;
      var col = i % cols;
      var row = Math.floor(i / cols);
      return {
        x: (col - (cols - 1) / 2) * slotW,
        y: viewportY(root, stage, zoneCenter) + (row - (rows - 1) / 2) * slotH,
        scale: scale
      };
    };
  }

  function buildTimeline(root, stage, deals, layout) {
    var n = deals.length;
    var totalDur = (n - 1) * RISE * OVERLAP + RISE;

    /* start: each card waits fully below the viewport, already at its final
       column and scale, so it rises straight up into place */
    deals.forEach(function (card, i) {
      var slot = layout(i, n);
      gsap.set(card, {
        xPercent: -50,
        yPercent: -50,
        x: slot.x,
        y: viewportY(root, stage, window.innerHeight * 1.18),
        scale: slot.scale,
        rotationZ: REST_ROT[i % REST_ROT.length],
        zIndex: 30 + i,
        force3D: true
      });
    });

    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: root,
        start: 'top top',
        end: function () {
          return '+=' + (totalDur * UNIT_VH * window.innerHeight) / 100;
        },
        pin: true,
        scrub: 1,
        anticipatePin: 1,
        invalidateOnRefresh: true
      }
    });

    var revealWindows = [];

    deals.forEach(function (card, i) {
      var at = i * RISE * OVERLAP;
      /* x and scale are function-based too, so a resize re-lands them
         correctly; pre-resize the rise reads as a clean vertical slide */
      tl.to(
        card,
        {
          x: function () { return layout(i, n).x; },
          y: function () { return layout(i, n).y; },
          scale: function () { return layout(i, n).scale; },
          duration: RISE,
          ease: 'power3.out'
        },
        at
      );
      revealWindows.push({ card: card, start: at + RISE * 0.6 });
    });

    /* class toggling driven off timeline time — scrub- and rewind-safe */
    tl.eventCallback('onUpdate', function () {
      var t = tl.time();
      revealWindows.forEach(function (w) {
        w.card.classList.toggle('is-revealed', t >= w.start);
      });
    });

    return tl;
  }

  /* ---------- cursor ring + hover tilt on revealed cards (fine pointers) ---------- */

  function buildCursor(root) {
    var ring = document.createElement('div');
    ring.className = 'deck-cursor';
    ring.setAttribute('aria-hidden', 'true');
    ring.innerHTML = '<span class="deck-cursor__dot"></span>';
    document.body.appendChild(ring);

    var ringX = gsap.quickTo(ring, 'x', { duration: 0.28, ease: 'power3.out' });
    var ringY = gsap.quickTo(ring, 'y', { duration: 0.28, ease: 'power3.out' });

    var tiltedFrame = null;

    function resetTilt() {
      if (tiltedFrame) {
        gsap.to(tiltedFrame, {
          rotationX: 0,
          rotationY: 0,
          duration: 0.5,
          ease: 'power2.out'
        });
        tiltedFrame = null;
      }
    }

    function onMove(event) {
      ringX(event.clientX);
      ringY(event.clientY);

      var hovered = event.target.closest && event.target.closest('[data-deck-card]');
      if (!hovered || !hovered.classList.contains('is-revealed')) {
        resetTilt();
        return;
      }
      var frame = hovered.querySelector('.deck-card__frame');
      if (!frame) return;
      if (tiltedFrame && tiltedFrame !== frame) resetTilt();
      tiltedFrame = frame;
      var rect = hovered.getBoundingClientRect();
      var dx = (event.clientX - (rect.left + rect.width / 2)) / rect.width;
      var dy = (event.clientY - (rect.top + rect.height / 2)) / rect.height;
      gsap.to(frame, {
        rotationX: gsap.utils.clamp(-5, 5, -dy * 8),
        rotationY: gsap.utils.clamp(-5, 5, dx * 8),
        transformPerspective: 900,
        duration: 0.4,
        ease: 'power2.out'
      });
    }

    function onEnter() { ring.classList.add('is-active'); }
    function onLeave() {
      ring.classList.remove('is-active');
      resetTilt();
    }
    function onDown() { ring.classList.add('is-pressed'); }
    function onUp() { ring.classList.remove('is-pressed'); }

    function onOver(event) {
      ring.classList.toggle(
        'is-hover',
        !!event.target.closest('.deck-card__quickadd, .deck-card__link')
      );
    }

    window.addEventListener('mousemove', onMove, { passive: true });
    root.addEventListener('mouseenter', onEnter);
    root.addEventListener('mouseleave', onLeave);
    root.addEventListener('mousedown', onDown);
    root.addEventListener('mouseup', onUp);
    root.addEventListener('mouseover', onOver);

    return function () {
      window.removeEventListener('mousemove', onMove);
      root.removeEventListener('mouseenter', onEnter);
      root.removeEventListener('mouseleave', onLeave);
      root.removeEventListener('mousedown', onDown);
      root.removeEventListener('mouseup', onUp);
      root.removeEventListener('mouseover', onOver);
      resetTilt();
      ring.remove();
    };
  }

  /* ---------- init ---------- */

  function init(root) {
    if (!root || root.dataset.deckInit) return;
    root.dataset.deckInit = '1';

    setupQuickAdd(root);

    if (!window.gsap || !window.ScrollTrigger) return;
    gsap.registerPlugin(ScrollTrigger);

    var stage = root.querySelector('[data-deck-stage]');
    var cards = Array.prototype.slice.call(
      root.querySelectorAll('[data-deck-card]')
    );
    if (!stage || cards.length === 0) return;

    /* reveal order = DOM / editor order, filling the grid row by row */
    var deals = cards;

    var mm = gsap.matchMedia();
    root._deckMatchMedia = mm;

    /* desktop: 3 columns × 2 rows */
    mm.add(
      '(min-width: 900px) and (prefers-reduced-motion: no-preference)',
      function () {
        root.classList.add('hustlers-deck--gsap', 'hustlers-deck--cursor');
        var layout = makeGrid(root, stage, cards, 3, {
          padX: 80,
          gap: 22,
          zonePad: 16,
          maxScale: 1
        });
        var tl = buildTimeline(root, stage, deals, layout);
        var killCursor = window.matchMedia('(pointer: fine)').matches
          ? buildCursor(root)
          : null;
        return function () {
          if (killCursor) killCursor();
          tl.scrollTrigger.kill();
          tl.kill();
          root.classList.remove('hustlers-deck--gsap', 'hustlers-deck--cursor');
          cards.forEach(function (c) { c.classList.remove('is-revealed'); });
        };
      }
    );

    /* mobile: 2 columns × 3 rows */
    mm.add(
      '(max-width: 899px) and (prefers-reduced-motion: no-preference)',
      function () {
        root.classList.add('hustlers-deck--gsap', 'hustlers-deck--grid-mobile');
        var layout = makeGrid(root, stage, cards, 2, {
          padX: 20,
          gap: 12,
          zonePad: 10,
          maxScale: 0.95
        });
        var tl = buildTimeline(root, stage, deals, layout);
        return function () {
          tl.scrollTrigger.kill();
          tl.kill();
          root.classList.remove('hustlers-deck--gsap', 'hustlers-deck--grid-mobile');
          cards.forEach(function (c) { c.classList.remove('is-revealed'); });
        };
      }
    );
  }

  function destroy(root) {
    if (!root) return;
    if (root._deckMatchMedia) {
      root._deckMatchMedia.revert();
      root._deckMatchMedia = null;
    }
    delete root.dataset.deckInit;
  }

  function initAll() {
    document.querySelectorAll('[data-hustlers-deck]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* theme editor lifecycle */
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-hustlers-deck]');
    if (root) init(root);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('[data-hustlers-deck]');
    if (root) destroy(root);
  });
})();
